import { makeAutoObservable, runInAction } from "mobx";
import type { ApiPlayable, CmsSection, CursorMeta } from "@hive/sdk";
import { i18next } from "../i18n";
import { hive } from "../lib/apiClient";
import { errorMessage } from "../lib/format";
import { playableKind, type PlayableKind } from "../lib/playableKind";
import { analyticsErrorCode, track } from "../lib/analytics";
import { loadViewerInteractionIds } from "../lib/viewerInteractions";
import type { PageData } from "../ssr/data";
import { sortCatalogue, type CatalogueOrder } from "../lib/catalogueOrder";
import { isPrivateDiscoverTab, type DiscoverTab } from "../lib/discoverTabs";
import { loadRecentGameHistory } from "../lib/recentGameHistory";

export type { DiscoverTab } from "../lib/discoverTabs";
export type KindFilter = PlayableKind | "all";

// Keep the first page aligned with SSR; subsequent pages fill a longer browsing session.
const INITIAL_PAGE_SIZE = 12;
const LOAD_MORE_PAGE_SIZE = 30;
/**
 * A chip's grid cap. Its CMS section ships at most 50 entries; a chip standing for one topic
 * tops the grid up from that topic's feed to this many (see `fetchTagSectionTopUp`).
 */
const TAG_SECTION_MAX_ITEMS = 100;
const ANONYMOUS_VIEWER_KEY = "anonymous";
/** Minimum gap between tail-recycle refetches while the feed stays dry. */
const RECYCLE_COOLDOWN_MS = 15_000;

function emptyMeta(): CursorMeta {
	return { nextCursor: null, hasMore: false };
}

/** A rendered CMS section that carries items (everything except the `FEED` instruction). */
export type CmsItemsSection = Exclude<CmsSection, { layout: "FEED" }>;

/**
 * A CMS section's feed-grid items: playables only. A creative (external banner) has no card
 * shape in the grid, so a creative-only section would open as an empty page — the category bar
 * uses this to decide whether a section becomes a chip at all, so bar and grid can't disagree.
 */
function sectionPlayables(section: CmsSection): ApiPlayable[] {
	if (section.layout === "FEED") return [];
	return section.items.flatMap((item) => (item.type === "playable" ? [item.playable] : []));
}

/**
 * The for-you feed marks every served item as "seen" for ~2h server-side, so
 * with a small catalog a page reload can legitimately come back empty. We keep
 * the last non-empty page around and replay it instead of an empty homepage.
 */
const FEED_CACHE_KEY = "muhaha:discover:last-feed";

function readFeedCache(): ApiPlayable[] {
	try {
		const raw = localStorage.getItem(FEED_CACHE_KEY);
		const parsed: unknown = raw ? JSON.parse(raw) : null;
		return Array.isArray(parsed) ? (parsed as ApiPlayable[]) : [];
	} catch {
		return [];
	}
}

function writeFeedCache(items: ApiPlayable[]) {
	try {
		localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(items));
	} catch {
		/* quota/private mode — cache is best-effort */
	}
}

export class DiscoverStore {
	tab: DiscoverTab = "forYou";
	topic: string | null = null;
	order: CatalogueOrder = "featured";
	kindFilter: KindFilter = "all";
	/**
	 * The category bar's operator-curated chips (CMS page `web-home-tags`), mirroring the
	 * app's `app-explore-tabs`: one section per chip, its title the label and its entries the
	 * grid shown while it is selected. Already reduced to what the bar can render.
	 */
	tagSections: CmsItemsSection[] = [];
	tagSectionsLoading = false;
	tagSectionsLoaded = false;
	/** Settles on success or failure so an unavailable CMS cannot leave the skeleton stuck. */
	tagSectionsSettled = false;
	tagSectionsError = false;
	private tagSectionsKey = "";
	private tagRequestId = 0;
	private appliedTagSection: CmsItemsSection | null = null;
	/** The selected chip's section id; null = the `tab` feed, or a `/t/:tag` topic. */
	tagSectionId: string | null = null;
	/**
	 * Operator-curated sections for the `home` CMS page, already reduced to what this
	 * page can render: `FEED` sections are an instruction to render the feed inline
	 * (this page already does that below) and empty sections have nothing to show.
	 */
	cmsSections: CmsSection[] = [];
	cmsLoading = false;
	cmsLoaded = false;
	items: ApiPlayable[] = [];
	meta: CursorMeta = emptyMeta();
	loading = false;
	loadingMore = false;
	error = "";
	likedIds = new Set<string>();
	favoritedIds = new Set<string>();
	/** Which viewer the current items were loaded for (see `ensureLoaded`). */
	loadedViewerKey: string | null = null;
	private contentRequestId = 0;
	/** When the last tail-recycle refetch ran (see `recycle`). */
	private lastRecycleAt = 0;

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	seed(data: NonNullable<PageData["discover"]>, userId: string | null, locale = "en") {
		this.tagSectionsKey = locale + ":" + (userId ?? "guest");
		this.tagRequestId++;
		this.tagSectionsError = data.tagsFailed === true;
		this.tagSectionsLoading = false;
		this.contentRequestId += 1;
		this.tab = data.tab ?? "forYou";
		this.tagSectionId = null;
		this.topic = data.topic;
		this.order = data.order ?? "featured";
		this.items = data.items;
		this.meta = data.meta;
		this.loadedViewerKey = userId ?? ANONYMOUS_VIEWER_KEY;
		this.cmsSections = data.sections.filter((s) => s.layout !== "FEED" && s.items.length > 0);
		this.tagSections = data.tags.filter((s): s is CmsItemsSection => s.layout !== "FEED" && !!s.title && sectionPlayables(s).length > 0);
		this.cmsLoaded = this.tagSectionsSettled = true;
		this.tagSectionsLoaded = !data.tagsFailed;
		this.tagSectionId = data.tagSectionId ?? null;
		this.appliedTagSection = this.tagSection;
		if (this.tagSection && !(this.order !== "featured" && this.tagSectionTopic)) {
			this.items = sortCatalogue(sectionPlayables(this.tagSection), this.order, (item) => item);
			this.meta = { nextCursor: null, hasMore: this.order === "featured" && this.tagSectionTopic !== null && this.items.length < TAG_SECTION_MAX_ITEMS };
		}
		this.loading = false;
		this.loadingMore = false;
		this.error = "";
	}

	get visibleItems() {
		return this.items.filter(
			(item) => this.kindFilter === "all" || playableKind(item) === this.kindFilter,
		);
	}

	/** The selected chip's section, or null when the bar is on a built-in tab or a topic. */
	get tagSection(): CmsItemsSection | null {
		return this.tagSections.find((section) => section.id === this.tagSectionId) ?? null;
	}

	/** The topic the selected chip stands for (a QUERY section on exactly one tag), if any. */
	get tagSectionTopic(): string | null {
		const target = this.tagSection?.headerTarget;
		return target?.type === "topic" ? target.tag : null;
	}

	get analyticsCategory() {
		return this.tagSection?.title ?? this.topic ?? "all";
	}

	get isEmpty() {
		return !this.loading && this.visibleItems.length === 0;
	}

	setTab(tab: DiscoverTab) {
		if (this.tab === tab && this.topic === null && this.tagSectionId === null) return;
		this.tab = tab;
		this.order = "featured";
		this.topic = null;
		this.tagSectionId = null;
		void this.loadInitial();
	}

	/** Select a feed and its viewer atomically, before any private request can start. */
	async loadTab(tab: DiscoverTab, userId: string | null) {
		const viewerKey = userId ?? ANONYMOUS_VIEWER_KEY;
		const changed = this.tab !== tab || this.topic !== null || this.tagSectionId !== null || this.loadedViewerKey !== viewerKey;
		this.tab = tab;
		this.order = "featured";
		this.topic = null;
		this.tagSectionId = null;
		if (isPrivateDiscoverTab(tab) && !userId) {
			this.reset();
			this.loadedViewerKey = viewerKey;
			return;
		}
		this.loadedViewerKey = viewerKey;
		// Returning from a game should show that play immediately.
		if (changed || tab === "recentlyPlayed") await this.loadInitial();
	}

	setTopic(topic: string | null, order: CatalogueOrder = "featured") {
		const normalized = topic?.trim().toLowerCase() || null;
		if (this.topic === normalized && this.order === order) return;
		this.topic = normalized;
		this.order = order;
		this.tagSectionId = null;
		void this.loadInitial();
	}

	/** Select an operator-curated chip; `null` returns to the current feed tab. */
	setTagSection(id: string | null, order: CatalogueOrder = "featured") {
		if (this.tagSectionId === id && this.topic === null && this.appliedTagSection === this.tagSection && this.order === order) return;
		this.tagSectionId = id;
		this.order = order;
		this.topic = null;
		void this.loadInitial();
	}

	setKind(kind: KindFilter) {
		this.kindFilter = kind;
	}

	reset() {
		this.contentRequestId += 1;
		this.lastRecycleAt = 0;
		this.items = [];
		this.meta = emptyMeta();
		this.loading = false;
		this.loadingMore = false;
		this.error = "";
		this.likedIds.clear();
		this.favoritedIds.clear();
		this.loadedViewerKey = null;
	}

	/**
	 * The category bar's chips. Like the strip below the feed this is pure decoration: an
	 * unconfigured page, a 404 or an offline device all leave the bar with its
	 * built-in tabs, and never write `error`. Only titled sections holding at least one
	 * playable become chips — anything else would open as a blank grid.
	 */
	async loadTagSections(force = false, locale = i18next.resolvedLanguage?.startsWith("zh") ? "zh" : "en", userId: string | null = null) {
		const key = locale + ":" + (userId ?? "guest");
		if (!force && key === this.tagSectionsKey && (this.tagSectionsLoading || this.tagSectionsLoaded)) return;
		if (key !== this.tagSectionsKey) {
			this.tagSections = [];
			this.tagSectionsLoaded = this.tagSectionsSettled = false;
		}
		this.tagSectionsKey = key;
		const requestId = ++this.tagRequestId;
		this.tagSectionsLoading = true;
		this.tagSectionsError = false;
		try {
			const page = await hive.pages.get("web-home-tags", { locale });
			if (requestId !== this.tagRequestId) return;
			runInAction(() => {
				this.tagSections = (page.sections ?? []).filter(
					(section): section is CmsItemsSection => ["HERO", "RAIL", "GRID"].includes(section.layout)
						&& !!section.title?.trim() && sectionPlayables(section).length > 0,
				);
				this.tagSectionsLoaded = true;
			});
		} catch {
			if (requestId === this.tagRequestId) runInAction(() => { this.tagSectionsError = true; });
		} finally {
			if (requestId === this.tagRequestId) runInAction(() => {
				this.tagSectionsLoading = false;
				this.tagSectionsSettled = true;
			});
		}
	}

	/**
	 * The CMS strip above the feed. Not personalized and entirely optional: an
	 * unconfigured page, a 404 or an offline device all leave `cmsSections` empty and
	 * never write `error`, so the page renders exactly as it did before CMS existed.
	 */
	async loadCmsSections(force = false) {
		if (this.cmsLoading || (this.cmsLoaded && !force)) return;
		this.cmsLoading = true;
		try {
			const page = await hive.pages.get("home");
			runInAction(() => {
				this.cmsSections = (page.sections ?? []).filter(
					(section) => section.layout !== "FEED" && section.items.length > 0,
				);
				this.cmsLoaded = true;
			});
		} catch {
			// Unconfigured page / 404 / offline — the strip stays hidden.
		} finally {
			runInAction(() => {
				this.cmsLoading = false;
			});
		}
	}

	/**
	 * The bar's refresh button. On a curated chip the entries came with the CMS page, so
	 * replaying them would refresh nothing — re-read the page first.
	 */
	async refresh() {
		if (this.tagSectionId) await this.loadTagSections(true);
		await this.loadInitial();
	}

	/**
	 * Load the feed once per viewer. The for-you tab marks served items as "seen"
	 * server-side, so refetching on every page mount would drain a small catalog
	 * to empty — keep what we have and only fetch for a new (or first) viewer.
	 */
	async ensureLoaded(userId: string | null) {
		void this.loadCmsSections(); // viewer-independent; runs alongside the feed request
		const viewerKey = userId ?? ANONYMOUS_VIEWER_KEY;
		if (this.loadedViewerKey === viewerKey) return;
		if (userId && this.loadedViewerKey === ANONYMOUS_VIEWER_KEY && !isPrivateDiscoverTab(this.tab)) {
			// Keep the public feed mounted across host sign-in so an active sandbox's
			// profile.login() call can receive its result instead of being torn down.
			this.loadedViewerKey = viewerKey;
			const [likedIds, favoritedIds] = await this.fetchViewerInteractions();
			runInAction(() => {
				this.likedIds = new Set(likedIds);
				this.favoritedIds = new Set(favoritedIds);
			});
			return;
		}
		this.loadedViewerKey = viewerKey;
		await this.loadInitial();
	}

	/**
	 * `hive.isAuthenticated()` used to answer this, but it only ever asked the SDK's token
	 * storage — which is a no-op now that the session is an HttpOnly cookie, so it would report
	 * "anonymous" for everyone and quietly drop every viewer's likes and favourites from the
	 * feed. `loadedViewerKey` is the right question anyway: it records whose feed this is, and
	 * every path into here sets it first (ensureLoaded, and loadInitial only ever runs after it).
	 */
	private async fetchViewerInteractions(): Promise<[string[], string[]]> {
		const hasViewer = this.loadedViewerKey !== null && this.loadedViewerKey !== ANONYMOUS_VIEWER_KEY;
		if (!hasViewer) return [[], []];
		return loadViewerInteractionIds();
	}

	async loadInitial() {
		const requestId = ++this.contentRequestId;
		const topic = this.topic;
		const tab = this.tab;
		this.appliedTagSection = this.tagSection;
		const curated = this.tagSection !== null;
		this.loading = true;
		this.loadingMore = false;
		this.lastRecycleAt = 0;
		this.error = "";
		this.items = [];
		this.meta = emptyMeta();
		try {
			const [page, [likedIds, favoritedIds]] = await Promise.all([
				this.fetchPage(undefined, topic, tab),
				this.fetchViewerInteractions(),
			]);
			if (requestId !== this.contentRequestId) return;
			runInAction(() => {
				let items = page.items;
				if (topic === null && tab === "forYou" && !curated) {
					if (items.length > 0) writeFeedCache(items);
					else items = readFeedCache();
				}
				this.items = items;
				this.meta = page.meta;
				this.likedIds = new Set(likedIds);
				this.favoritedIds = new Set(favoritedIds);
			});
		} catch (error) {
			if (requestId !== this.contentRequestId) return;
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("discover.loadFailed"));
			});
		} finally {
			if (requestId === this.contentRequestId) {
				runInAction(() => {
					this.loading = false;
				});
			}
		}
	}

	async loadMore() {
		if (this.loading || this.loadingMore || !this.meta.hasMore) return;
		const requestId = this.contentRequestId;
		const topic = this.topic;
		const tab = this.tab;
		const topUpTag = this.order === "featured" ? this.tagSectionTopic : null;
		this.loadingMore = true;
		try {
			const page = topUpTag
				? await this.fetchTagSectionTopUp(topUpTag, this.items)
				: await this.fetchPage(this.meta.nextCursor ?? undefined, topic, tab, this.order, LOAD_MORE_PAGE_SIZE);
			if (requestId !== this.contentRequestId) return;
			runInAction(() => {
				const known = new Set(this.items.map((i) => i.id));
				this.items = [...this.items, ...page.items.filter((i) => !known.has(i.id))];
				this.meta = page.meta;
			});
		} catch (error) {
			if (requestId !== this.contentRequestId) return;
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("common.loadMoreFailed"));
			});
		} finally {
			if (requestId === this.contentRequestId) {
				runInAction(() => {
					this.loadingMore = false;
				});
			}
		}
	}

	/**
	 * Tail fallback for the player's endless pager: once the feed reports
	 * `hasMore: false` its cursor is spent — but a FRESH first page is not. The
	 * server re-shuffles anonymous feeds per request and its seen-window keeps
	 * expiring, so a later page one can legitimately serve items this cursor
	 * sequence never did. When the fresh page holds anything new, it appends
	 * after the current tail and `meta` adopts the fresh cursor, so normal
	 * paging resumes on the new sequence. Failures and dry responses are silent
	 * (the pager wraps around instead), and the cooldown keeps a dry catalog
	 * from being refetched on every flip near the tail.
	 */
	async recycle() {
		if (this.loading || this.loadingMore || this.meta.hasMore) return;
		if (this.tab === "recentlyPlayed") return;
		// A curated chip is its CMS entries plus at most one topic top-up — nothing fresh to fetch.
		if (this.tagSection) return;
		const now = Date.now();
		if (now - this.lastRecycleAt < RECYCLE_COOLDOWN_MS) return;
		this.lastRecycleAt = now;
		const requestId = this.contentRequestId;
		this.loadingMore = true;
		try {
			const page = await this.fetchPage(undefined, this.topic, this.tab);
			if (requestId !== this.contentRequestId) return;
			runInAction(() => {
				const known = new Set(this.items.map((i) => i.id));
				const fresh = page.items.filter((i) => !known.has(i.id));
				// A dry page keeps the spent meta: adopting the fresh cursor with
				// nothing appended would flip hasMore back on with the pager parked at
				// the tail — nothing re-triggers loadMore there, and the wrap gate
				// (`!hasMore`) would lock the pager on its last slide.
				if (fresh.length > 0) {
					this.items = [...this.items, ...fresh];
					this.meta = page.meta;
				}
			});
		} catch {
			// Best-effort refetch — the pager wraps around on a dry tail either way.
		} finally {
			if (requestId === this.contentRequestId) {
				runInAction(() => {
					this.loadingMore = false;
				});
			}
		}
	}

	fetchPage(cursor?: string, topic = this.topic, tab = this.tab, order = this.order, limit = INITIAL_PAGE_SIZE) {
		// A chip ships its entry list (≤50) with the CMS page; only a topic chip has more to load.
		const section = this.tagSection;
		const sortedTag = topic ?? this.tagSectionTopic;
		if (order !== "featured" && sortedTag) return hive.feed.forYou({
			tag: sortedTag, sort: order, kinds: ["game", "drama"], limit, cursor,
		});
		if (section) {
			const items = sortCatalogue(sectionPlayables(section), order, (item) => item);
			const hasMore = order === "featured" && this.tagSectionTopic !== null && items.length < TAG_SECTION_MAX_ITEMS;
			return Promise.resolve({ items, meta: { nextCursor: null, hasMore } });
		}
		if (topic) return hive.tags.playables(topic, { limit, cursor });
		if (isPrivateDiscoverTab(tab) && (!this.loadedViewerKey || this.loadedViewerKey === ANONYMOUS_VIEWER_KEY)) {
			return Promise.resolve({ items: [] as ApiPlayable[], meta: emptyMeta() });
		}
		if (tab === "recentlyPlayed") {
			const requestId = this.contentRequestId;
			return loadRecentGameHistory((next) => hive.history.list({ limit, cursor: next }), cursor,
				() => requestId === this.contentRequestId);
		}
		if (tab === "latest" || tab === "popular") return hive.feed.forYou({
			sort: tab === "latest" ? "latest" : "hot", kind: "game", limit, cursor,
		});
		// Both tabs page by cursor: the anonymous for-you cursor carries the shuffle
		// seed, so dropping it here would re-serve a freshly shuffled page one.
		const params = { limit, cursor };
		return tab === "following" ? hive.feed.following(params) : hive.feed.forYou(params);
	}

	/**
	 * Tops a topic chip's CMS entries up from the topic's newest-first feed. A single page of
	 * TAG_SECTION_MAX_ITEMS always holds enough unseen items to reach the cap — at most `shown`
	 * of them repeat — so one request either fills the grid or exhausts the topic; nothing is
	 * left to page after it.
	 */
	private async fetchTagSectionTopUp(tag: string, shown: ApiPlayable[]) {
		const page = await hive.tags.playables(tag, { limit: TAG_SECTION_MAX_ITEMS });
		const known = new Set(shown.map((item) => item.id));
		const items = page.items
			.filter((item) => !known.has(item.id))
			.slice(0, TAG_SECTION_MAX_ITEMS - shown.length);
		return { items, meta: emptyMeta() };
	}

	isLiked(id: string) {
		return this.likedIds.has(id);
	}

	isFavorited(id: string) {
		return this.favoritedIds.has(id);
	}

	setLiked(id: string, liked: boolean) {
		if (liked) this.likedIds.add(id);
		else this.likedIds.delete(id);
	}

	setFavorited(id: string, favorited: boolean) {
		if (favorited) this.favoritedIds.add(id);
		else this.favoritedIds.delete(id);
	}

	setViewerInteractions(likedIds: string[], favoritedIds: string[]) {
		this.likedIds = new Set(likedIds);
		this.favoritedIds = new Set(favoritedIds);
	}

	async toggleLike(id: string) {
		const item = this.items.find((candidate) => candidate.id === id);
		const wasLiked = this.likedIds.has(id);
		runInAction(() => {
			this.setLiked(id, !wasLiked);
			this.patch(id, (item) => ({
				...item,
				likeCount: Math.max(0, item.likeCount + (wasLiked ? -1 : 1)),
			}));
		});
		try {
			if (wasLiked) await hive.playables.unlike(id);
			else await hive.playables.like(id);
			if (item) {
				track("content_action_result", {
					content_id: id,
					content_kind: playableKind(item),
					source: "discover",
					feed_tab: this.tab,
					category: this.analyticsCategory,
					action: wasLiked ? "unlike" : "like",
					result: "success",
				});
			}
		} catch (error) {
			if (item) {
				track("content_action_result", {
					content_id: id,
					content_kind: playableKind(item),
					source: "discover",
					feed_tab: this.tab,
					category: this.analyticsCategory,
					action: wasLiked ? "unlike" : "like",
					result: "failure",
					error_code: analyticsErrorCode(error),
				});
			}
			runInAction(() => {
				this.setLiked(id, wasLiked);
				this.patch(id, (item) => ({
					...item,
					likeCount: Math.max(0, item.likeCount + (wasLiked ? 1 : -1)),
				}));
			});
		}
	}

	async toggleFavorite(id: string) {
		const item = this.items.find((candidate) => candidate.id === id);
		const wasFav = this.favoritedIds.has(id);
		runInAction(() => {
			this.setFavorited(id, !wasFav);
			this.patch(id, (item) => ({
				...item,
				favoriteCount: Math.max(0, item.favoriteCount + (wasFav ? -1 : 1)),
			}));
		});
		try {
			if (wasFav) await hive.playables.unfavorite(id);
			else await hive.playables.favorite(id);
			if (item) {
				track("content_action_result", {
					content_id: id,
					content_kind: playableKind(item),
					source: "discover",
					feed_tab: this.tab,
					category: this.analyticsCategory,
					action: wasFav ? "unfavorite" : "favorite",
					result: "success",
				});
			}
		} catch (error) {
			if (item) {
				track("content_action_result", {
					content_id: id,
					content_kind: playableKind(item),
					source: "discover",
					feed_tab: this.tab,
					category: this.analyticsCategory,
					action: wasFav ? "unfavorite" : "favorite",
					result: "failure",
					error_code: analyticsErrorCode(error),
				});
			}
			runInAction(() => {
				this.setFavorited(id, wasFav);
				this.patch(id, (item) => ({
					...item,
					favoriteCount: Math.max(0, item.favoriteCount + (wasFav ? 1 : -1)),
				}));
			});
		}
	}

	patch(id: string, updater: (item: ApiPlayable) => ApiPlayable) {
		this.items = this.items.map((item) => (item.id === id ? updater(item) : item));
	}

	remove(id: string) {
		this.items = this.items.filter((item) => item.id !== id);
		this.likedIds.delete(id);
		this.favoritedIds.delete(id);
		const cached = readFeedCache();
		if (cached.some((item) => item.id === id)) {
			writeFeedCache(cached.filter((item) => item.id !== id));
		}
	}
}

export const discoverStore = new DiscoverStore();
